/* eslint-disable no-param-reassign */
import React from "react";
import dayjs from "dayjs";
import { makeObservable, observable, action } from "mobx";
import { Cookies } from "react-cookie";
import moment from "moment";
import { Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Props as DataGridProps } from "../components/DataGrid/DataGrid";
import { getPopularKeywords, getKeywordTemplates } from "../api/Keywords";
import * as Assets from "../components/Icon/assets";
import TemplateVideo from "../pages/Keywords/Modules/TemplateVideo";
import {
  KeywordSortBy,
  KeywordMatchType,
  KeywordTemplate,
  PopularKeyword,
  DEFAULT_SORT_BY,
  DEFAULT_MATCH_TYPE,
  MATCH_TYPE_OPTIONS,
  defaultDateRange,
  LIMIT_RANGE,
  TEMPLATES_PER_KEYWORD_RANGE,
  DISPLAY_RANGE,
  KEYWORD_SORT_OPTIONS,
  buildKeywordDateRange,
  validateKeywordRange,
  isPopularResponse,
  isKeywordTemplatesResponse,
  describeKeywordError,
  normalizeKeyword,
  parseKeywordSearch,
  keywordPath,
} from "../pages/Keywords/keywordParams";

const cookies = new Cookies();
const getCookie = (item: string) => cookies.get(item);
const cookieOptions = () => ({
  path: "/",
  expires: moment().add("100", "y").toDate(),
});

/** 다른 탭과 충돌하지 않게 Keywords 쿠키는 모두 kw_ 접두어를 쓴다. */
export const KEYWORD_COOKIES = [
  "kw_country",
  "kw_matchType",
  "kw_startDate",
  "kw_endDate",
  "kw_dateExplicit",
  "kw_limit",
  "kw_tpk",
  "kw_sortBy",
  "kw_rowsPerPage",
] as const;

/** 기본 국가. 이 기능의 첫 대상이 인도라 IN 을 기본으로 둔다. */
export const DEFAULT_COUNTRY = "IN";

const readNumberCookie = (
  name: string,
  range: { min: number; max: number; default: number }
): number => {
  const n = Number(getCookie(name));
  if (!Number.isInteger(n) || n < range.min || n > range.max) {
    return range.default;
  }
  return n;
};

const readSortCookie = (name: string): KeywordSortBy => {
  const v = getCookie(name);
  return KEYWORD_SORT_OPTIONS.some((o) => o.value === v)
    ? (v as KeywordSortBy)
    : DEFAULT_SORT_BY;
};

const readMatchTypeCookie = (name: string): KeywordMatchType => {
  const v = getCookie(name);
  return MATCH_TYPE_OPTIONS.some((o) => o.value === v)
    ? (v as KeywordMatchType)
    : DEFAULT_MATCH_TYPE;
};

const readDateCookie = (name: string, fallback: Date): Date => {
  const v = getCookie(name);
  return v && dayjs(v).isValid() ? dayjs(v).toDate() : fallback;
};

/**
 * 사용자가 피커로 날짜를 직접 골랐는지. "1" 이면 kw_startDate / kw_endDate 를
 * 그대로 복원하고, 아니면 매번 "최근 30일 ~ 오늘" 을 새로 계산한다(AI Review 와
 * 같은 방식). 날짜를 고정 저장하면 하루가 지난 뒤 종료일이 어제로 남아
 * 오늘 게시분이 빠진다.
 */
const isExplicitDateCookie = (): boolean => getCookie("kw_dateExplicit") === "1";

const resolveDates = (): { startDate: Date; endDate: Date } => {
  const defaults = defaultDateRange();
  if (!isExplicitDateCookie()) return defaults;
  return {
    startDate: readDateCookie("kw_startDate", defaults.startDate),
    endDate: readDateCookie("kw_endDate", defaults.endDate),
  };
};

export interface PopularState {
  loading: boolean;
  /** 서버가 적용한 조회 범위 (RFC3339). 응답 전에는 빈 문자열. */
  startedAt: string;
  endedAt: string;
  country: string;
  /** 서버가 적용한 키워드 기준(tag / text). 응답 전에는 요청값. */
  matchType: KeywordMatchType;
  /** 이 결과를 조회할 때 쓴 sortBy. 현재 sortBy 와 다르면 결과가 낡은 것이다. */
  sortBy: KeywordSortBy;
  scannedTemplates: number;
  keywords: PopularKeyword[];
  /** 아직 한 번도 조회하지 않았으면 false. 빈 결과와 구분하기 위함. */
  fetched: boolean;
}

export interface KeywordDetailState {
  /** 정규화된 해시태그. 빈 문자열이면 드릴다운이 열리지 않은 상태. */
  keyword: string;
  templateCount: number;
}

class KeywordsStore {
  constructor() {
    makeObservable(this, {
      country: observable,
      matchType: observable,
      startDate: observable,
      endDate: observable,
      isDateExplicit: observable,
      limit: observable,
      templatesPerKeyword: observable,
      sortBy: observable,
      popular: observable,
      detail: observable,
      gridOption: observable,
    });
  }

  // ---- 필터 (다이얼로그에서 편집, OK 시 쿠키로 확정) ----
  country: string = getCookie("kw_country") || DEFAULT_COUNTRY;
  matchType: KeywordMatchType = readMatchTypeCookie("kw_matchType");
  isDateExplicit: boolean = isExplicitDateCookie();
  startDate: Date = resolveDates().startDate;
  endDate: Date = resolveDates().endDate;
  limit: number = readNumberCookie("kw_limit", LIMIT_RANGE);
  templatesPerKeyword: number = readNumberCookie(
    "kw_tpk",
    TEMPLATES_PER_KEYWORD_RANGE
  );
  /**
   * 템플릿 정렬 기준. 인기 목록과 드릴다운이 하나를 공유한다 — 어느 화면의 지표
   * 헤더(View / Like / Download / Share / AI Score)를 눌러도 같은 값이 바뀌고,
   * 다른 화면으로 돌아가면 그 값으로 다시 조회한다. 필터 다이얼로그에는 없다.
   */
  sortBy: KeywordSortBy = readSortCookie("kw_sortBy");

  // ---- 인기 키워드 결과 ----
  popular: PopularState = {
    loading: false,
    startedAt: "",
    endedAt: "",
    country: "",
    matchType: DEFAULT_MATCH_TYPE,
    sortBy: DEFAULT_SORT_BY,
    scannedTemplates: 0,
    keywords: [],
    fetched: false,
  };

  // ---- 키워드 드릴다운 ----
  detail: KeywordDetailState = {
    keyword: "",
    templateCount: 0,
  };

  gridOption: DataGridProps = {
    columns: [
      {
        field: "index",
        headerName: "No",
        width: 60,
        align: "center",
        sortable: false,
        renderCell: (params) =>
          params.api.getRowIndex(params.row.id) +
          1 +
          this.gridOption.page * this.gridOption.rowsPerPage,
      },
      {
        field: "video",
        headerName: "Video",
        width: 130,
        sortable: false,
        renderCell: (params: any) => (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            <TemplateVideo src={params.row.videoPath} height={100} />
          </div>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        minWidth: 240,
        sortable: false,
        renderCell: (params: any) => (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {params.row.description}
          </Typography>
        ),
      },
      {
        field: "viewCount",
        headerName: "View",
        width: 110,
        sortable: false,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => Number(value ?? 0).toLocaleString(),
        renderHeader: (params) => this.renderSortHeader(params, "view"),
      },
      {
        field: "likeCounts",
        headerName: "Like",
        width: 100,
        sortable: false,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => Number(value ?? 0).toLocaleString(),
        renderHeader: (params) => this.renderSortHeader(params, "like"),
      },
      {
        field: "downloadCounts",
        headerName: "Download",
        width: 110,
        sortable: false,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => Number(value ?? 0).toLocaleString(),
        renderHeader: (params) => this.renderSortHeader(params, "download"),
      },
      {
        field: "shareCounts",
        headerName: "Share",
        width: 100,
        sortable: false,
        align: "right",
        headerAlign: "right",
        valueFormatter: ({ value }) => Number(value ?? 0).toLocaleString(),
        renderHeader: (params) => this.renderSortHeader(params, "share"),
      },
      {
        field: "aiTotal",
        headerName: "Total",
        width: 90,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params: any) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {params.row.aiScore ? params.row.aiScore.total : "N/A"}
          </Typography>
        ),
        renderHeader: (params) => this.renderSortHeader(params, "aiScore"),
      },
      {
        field: "aiQuality",
        headerName: "Quality",
        width: 90,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params: any) => (
          <Typography variant="body2">
            {params.row.aiScore ? params.row.aiScore.quality : "N/A"}
          </Typography>
        ),
      },
      {
        field: "aiEditing",
        headerName: "Editing",
        width: 90,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params: any) => (
          <Typography variant="body2">
            {params.row.aiScore ? params.row.aiScore.editing : "N/A"}
          </Typography>
        ),
      },
      {
        field: "link",
        headerName: "Link",
        width: 80,
        sortable: false,
        renderCell: () => (
          <img
            src={Assets.NewWindows}
            alt="new window"
            style={{ width: 20, height: 20, cursor: "pointer" }}
          />
        ),
      },
    ],
    rows: [],
    totalCount: 0,
    loading: false,
    page: 0,
    rowsPerPage: readNumberCookie("kw_rowsPerPage", DISPLAY_RANGE),
    columnGroupingModel: [
      {
        groupId: "aiScore",
        headerName: "AI Score",
        children: [
          { field: "aiTotal" },
          { field: "aiQuality" },
          { field: "aiEditing" },
        ],
      },
    ],
  };

  // ---- 필터 setter ----
  setCountry = action((country: string) => {
    this.country = country.toUpperCase();
  });

  setMatchType = action((matchType: KeywordMatchType) => {
    this.matchType = matchType;
  });

  /**
   * 검색 입력 제출 또는 라우트 진입. 첫 글자가 # 이면 해시태그, 아니면 본문 텍스트
   * 검색으로 키워드 유형을 맞추고(쿠키에도 확정) 드릴다운을 연다. 돌려주는 값은
   * 이동할 경로, 정리 후 빈 값이면 null.
   */
  searchKeyword = action((raw: string): string | null => {
    const parsed = parseKeywordSearch(raw);
    if (!parsed) return null;
    if (parsed.matchType !== this.matchType) {
      this.matchType = parsed.matchType;
      cookies.set("kw_matchType", parsed.matchType, cookieOptions());
    }
    this.openKeyword(parsed.keyword);
    return keywordPath(parsed.keyword, parsed.matchType);
  });

  setStartDate = action((date: Date) => {
    this.startDate = date;
  });

  setEndDate = action((date: Date) => {
    this.endDate = date;
  });

  setDateExplicit = action((value: boolean) => {
    this.isDateExplicit = value;
  });

  /** "최근 30일 ~ 오늘" 로 되돌린다(비명시). 다이얼로그의 리셋 버튼이 쓴다. */
  applyDefaultDates = action(() => {
    const { startDate, endDate } = defaultDateRange();
    this.startDate = startDate;
    this.endDate = endDate;
    this.isDateExplicit = false;
  });

  /**
   * 비명시 상태면 조회 직전에 기간을 다시 계산한다. 탭을 오래 열어 두었거나 날짜가
   * 바뀐 뒤에도 종료일이 항상 "오늘"(→ 오늘 23:59:59.999Z 까지)이 되게 한다.
   */
  private refreshRollingDates() {
    if (this.isDateExplicit) return;
    const { startDate, endDate } = defaultDateRange();
    this.startDate = startDate;
    this.endDate = endDate;
  }

  setLimit = action((limit: number) => {
    this.limit = limit;
  });

  setTemplatesPerKeyword = action((n: number) => {
    this.templatesPerKeyword = n;
  });

  /**
   * 지표 헤더 클릭. 정렬 키를 바꾸고 쿠키에 남긴다. 바뀌었으면 true 를 돌려주며
   * 호출한 화면이 자기 조회를 다시 한다. 같은 키를 다시 눌러도 서버가 내림차순만
   * 지원하므로 false(아무것도 안 함).
   */
  setSortBy = action((sortBy: KeywordSortBy): boolean => {
    if (sortBy === this.sortBy) return false;
    this.sortBy = sortBy;
    cookies.set("kw_sortBy", sortBy, cookieOptions());
    return true;
  });

  /** 인기 목록 헤더 클릭: 정렬을 바꾸고 인기 목록을 다시 조회한다. */
  sortPopularBy = action((sortBy: KeywordSortBy) => {
    if (!this.setSortBy(sortBy)) return;
    this.fetchPopular();
  });

  /**
   * 드릴다운 헤더 클릭: 정렬을 바꾸고 1 페이지부터 다시 조회한다.
   * page 가 0 이 아니면 page 만 0 으로 되돌린다 — 페이지 변경 effect 가 조회를
   * 이어받으므로 여기서 또 부르면 두 번 조회된다.
   */
  sortKeywordTemplatesBy = action((sortBy: KeywordSortBy) => {
    if (!this.setSortBy(sortBy)) return;
    if (this.gridOption.page !== 0) {
      this.gridOption.page = 0;
      return;
    }
    this.fetchKeywordTemplates();
  });

  /** 다이얼로그 Cancel: 확정된(쿠키) 값으로 되돌린다. */
  resetFilters = action(() => {
    this.country = getCookie("kw_country") || DEFAULT_COUNTRY;
    this.matchType = readMatchTypeCookie("kw_matchType");
    this.isDateExplicit = isExplicitDateCookie();
    const dates = resolveDates();
    this.startDate = dates.startDate;
    this.endDate = dates.endDate;
    this.limit = readNumberCookie("kw_limit", LIMIT_RANGE);
    this.templatesPerKeyword = readNumberCookie(
      "kw_tpk",
      TEMPLATES_PER_KEYWORD_RANGE
    );
  });

  /**
   * 다이얼로그 OK: 서버에 보내기 전에 검증하고, 통과하면 쿠키에 확정한 뒤 조회한다.
   * 위반 시 메시지를 돌려주고 아무것도 바꾸지 않는다(다이얼로그는 열린 채로 둔다).
   */
  applyFilters = action((): string | null => {
    if (!this.country || this.country.length !== 2) {
      return "Please select a country.";
    }
    const rangeError = validateKeywordRange(this.startDate, this.endDate);
    if (rangeError) return rangeError;
    if (
      !Number.isInteger(this.limit) ||
      this.limit < LIMIT_RANGE.min ||
      this.limit > LIMIT_RANGE.max
    ) {
      return `Keywords must be between ${LIMIT_RANGE.min} and ${LIMIT_RANGE.max}.`;
    }
    if (
      !Number.isInteger(this.templatesPerKeyword) ||
      this.templatesPerKeyword < TEMPLATES_PER_KEYWORD_RANGE.min ||
      this.templatesPerKeyword > TEMPLATES_PER_KEYWORD_RANGE.max
    ) {
      return `Templates per keyword must be between ${TEMPLATES_PER_KEYWORD_RANGE.min} and ${TEMPLATES_PER_KEYWORD_RANGE.max}.`;
    }

    cookies.set("kw_country", this.country, cookieOptions());
    cookies.set("kw_matchType", this.matchType, cookieOptions());
    // 날짜는 사용자가 직접 골랐을 때만 고정 저장한다. 비명시면 플래그만 "0" 으로
    // 남겨 다음 진입에서 "최근 30일 ~ 오늘" 이 새로 계산되게 한다.
    cookies.set("kw_dateExplicit", this.isDateExplicit ? "1" : "0", cookieOptions());
    if (this.isDateExplicit) {
      cookies.set("kw_startDate", this.startDate, cookieOptions());
      cookies.set("kw_endDate", this.endDate, cookieOptions());
    } else {
      cookies.remove("kw_startDate", { path: "/" });
      cookies.remove("kw_endDate", { path: "/" });
    }
    cookies.set("kw_limit", this.limit, cookieOptions());
    cookies.set("kw_tpk", this.templatesPerKeyword, cookieOptions());

    this.fetchPopular();
    return null;
  });

  // ---- 인기 키워드 조회 ----
  fetchPopular = action(async () => {
    this.refreshRollingDates();
    const { startedAt, endedAt } = buildKeywordDateRange(
      this.startDate,
      this.endDate
    );
    this.popular = { ...this.popular, loading: true, keywords: [] };

    const res = await getPopularKeywords({
      country: this.country,
      matchType: this.matchType,
      startedAt,
      endedAt,
      limit: this.limit,
      templatesPerKeyword: this.templatesPerKeyword,
      sortBy: this.sortBy,
    });

    if (!isPopularResponse(res)) {
      this.setPopular({
        loading: false,
        keywords: [],
        scannedTemplates: 0,
        fetched: true,
      });
      alert(describeKeywordError(res, "Failed to load popular keywords."));
      return;
    }

    this.setPopular({
      loading: false,
      fetched: true,
      country: res.country,
      matchType: res.matchType ?? this.matchType,
      sortBy: this.sortBy,
      startedAt: res.startedAt,
      endedAt: res.endedAt,
      scannedTemplates: res.scannedTemplates,
      keywords: res.keywords,
    });
  });

  setPopular = action((patch: Partial<PopularState>) => {
    this.popular = { ...this.popular, ...patch };
  });

  // ---- 키워드 드릴다운 ----
  /**
   * 드릴다운 진입. 다른 키워드로 바뀌면 page 를 0 으로 되돌린다. 정렬은 공유
   * sortBy 를 그대로 쓰므로 인기 목록의 대표 템플릿 순서와 첫 페이지가 일치한다.
   */
  openKeyword = action((rawKeyword: string) => {
    const keyword = normalizeKeyword(rawKeyword);
    if (keyword === this.detail.keyword) return;
    this.detail = { keyword, templateCount: 0 };
    this.gridOption = {
      ...this.gridOption,
      page: 0,
      rows: [],
      totalCount: 0,
    };
  });

  setGridOption = action((patch: Partial<DataGridProps>) => {
    this.gridOption = { ...this.gridOption, ...patch };
  });

  onPageChange = action((page: number) => {
    window.scrollTo(0, 0);
    this.gridOption.page = page;
  });

  onPageSizeChange = action((rowsPerPage: number) => {
    window.scrollTo(0, 0);
    this.gridOption.rowsPerPage = rowsPerPage;
    cookies.set("kw_rowsPerPage", rowsPerPage, cookieOptions());
  });

  fetchKeywordTemplates = action(async () => {
    if (!this.detail.keyword) return;
    this.refreshRollingDates();
    const { startedAt, endedAt } = buildKeywordDateRange(
      this.startDate,
      this.endDate
    );
    this.gridOption.loading = true;
    this.gridOption.rows = [];

    const res = await getKeywordTemplates({
      country: this.country,
      keyword: this.detail.keyword,
      matchType: this.matchType,
      startedAt,
      endedAt,
      page: this.gridOption.page + 1,
      display: this.gridOption.rowsPerPage,
      sortBy: this.sortBy,
    });

    if (!isKeywordTemplatesResponse(res)) {
      this.setGridOption({ loading: false, rows: [], totalCount: 0 });
      this.detail.templateCount = 0;
      alert(
        describeKeywordError(
          res,
          `Failed to load templates for "${this.detail.keyword}".`
        )
      );
      return;
    }

    const rows = res.templates.map((t: KeywordTemplate) => ({
      ...t,
      id: t.templateId,
    }));
    this.detail.templateCount = res.templateCount;
    this.setGridOption({
      loading: false,
      rows,
      totalCount: res.templateCount,
    });
  });

  /**
   * 서버는 내림차순 정렬만 지원하므로 헤더 클릭은 정렬 키만 바꾼다. 활성 키에는
   * 아래 화살표를, 나머지에는 흐린 정렬 아이콘을 표시한다.
   */
  renderSortHeader = (params: any, key: KeywordSortBy) => {
    const active = this.sortBy === key;
    return (
      <div
        style={{
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        {params.colDef.headerName}
        {active ? (
          <ArrowDownwardIcon fontSize="small" style={{ marginLeft: 5 }} />
        ) : (
          <UnfoldMoreIcon
            fontSize="small"
            style={{ marginLeft: 5, opacity: 0.35 }}
          />
        )}
      </div>
    );
  };
}

export default KeywordsStore;
